import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Users, BookOpen, Briefcase, Sun, Moon, Globe2 } from 'lucide-react';
import { ServiceLink } from './types';
import { useTheme } from '../../contexts/ThemeContext';

interface DesktopNavProps {
  serviceLinks: ServiceLink[];
  language: 'en' | 'de';
  onLanguageToggle: () => void;
  onScrollToSection: (sectionId: string) => void;
  onNavigate: (path: string) => void;
}

const DesktopNav: React.FC<DesktopNavProps> = ({
  serviceLinks,
  language,
  onLanguageToggle,
  onScrollToSection,
  onNavigate
}) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    // Scroll to top after navigation
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className="hidden md:flex items-center space-x-6">
      {/* Services Dropdown */}
      <div className="relative group">
        <div className="flex items-center space-x-1 text-primary-mint hover:text-primary-green transition-colors cursor-pointer">
          <span>Services</span>
          <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
        </div>

        <div className="absolute top-full left-0 mt-2 w-80 bg-secondary-black/95 backdrop-blur-md 
                     border border-primary-mint/10 rounded-xl shadow-xl overflow-hidden
                     opacity-0 invisible group-hover:opacity-100 group-hover:visible
                     transition-all duration-300 transform origin-top scale-95 
                     group-hover:scale-100">
          <div className="p-2">
            {serviceLinks.map((service, index) => (
              <button
                key={index}
                onClick={() => handleNavigation(service.path)}
                className="flex flex-col w-full text-left px-4 py-3 rounded-lg hover:bg-primary-mint/10 
                         transition-colors group/item"
              >
                <span className="text-primary-mint group-hover/item:text-primary-green 
                             transition-colors font-medium">
                  {service.title}
                </span>
                <span className="text-sm text-secondary-cream/60 group-hover/item:text-secondary-cream/80 
                             transition-colors">
                  {service.description}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Links */}
      <button 
        onClick={() => handleNavigation('/about')}
        className="flex items-center space-x-2 text-primary-mint hover:text-primary-green 
                 transition-colors group"
      >
        <Users className="w-4 h-4 group-hover:scale-110 transition-transform" />
        <span>About Us</span>
      </button>

      <button 
        onClick={() => handleNavigation('/blog')}
        className="flex items-center space-x-2 text-primary-mint hover:text-primary-green 
                 transition-colors group"
      >
        <BookOpen className="w-4 h-4 group-hover:scale-110 transition-transform" />
        <span>Blog</span>
      </button>

      <button 
        onClick={() => handleNavigation('/career')}
        className="flex items-center space-x-2 text-primary-mint hover:text-primary-green 
                 transition-colors group"
      >
        <Briefcase className="w-4 h-4 group-hover:scale-110 transition-transform" />
        <span>Careers</span>
      </button>

      <button 
        onClick={() => onScrollToSection('contact')}
        className="bg-primary-mint/10 border border-primary-mint/30 px-6 py-3 rounded-full 
                 text-primary-mint hover:bg-primary-mint/20 transition-all"
      >
        Contact
      </button>

      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="p-2 text-primary-mint hover:text-primary-green transition-colors"
        aria-label="Toggle theme"
      >
        {isDarkMode ? 
          <Sun className="w-5 h-5" /> : 
          <Moon className="w-5 h-5" />
        }
      </button>

      {/* Language Toggle */}
      <button
        onClick={onLanguageToggle}
        className="flex items-center space-x-2 text-primary-mint hover:text-primary-green 
                 transition-colors"
        aria-label="Toggle language"
      >
        <Globe2 className="w-5 h-5" />
        <span className="text-sm font-medium">{language.toUpperCase()}</span>
      </button>
    </nav>
  );
};

export default DesktopNav;